import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import CounselService, {
  CounselCorpusDocMeta,
  CounselCorpusDoc,
  CounselGlossaryEntry,
} from "../../services/CounselService";
import ApiService from "../../_services/ApiService";
import "./knowledge-core.css";

interface RagCorpusDoc {
  studyResourceId: number;
  documentName: string | null;
  resourceType: string | null;
  lessonPlanId: number;
  moduleName: string | null;
  status: "pending" | "processing" | "completed" | "failed";
  chunks: number;
  vectors: number;
  errorMessage: string | null;
  indexedAt: string;
}

interface RagCorpusSummary {
  documents: number;
  indexed: number;
  failed: number;
  totalVectors: number;
  modules: number;
}

const DOC_TYPES = ["call_summary", "transcript", "playbook", "framework", "other"];
const TYPE_LABEL: Record<string, string> = {
  call_summary: "Call summary",
  transcript: "Transcript",
  playbook: "Playbook",
  framework: "Framework",
  other: "Other",
};

const emptyForm = {
  title: "",
  docType: "call_summary",
  callType: "",
  segment: "",
  studentLabel: "",
  gradeLabel: "",
  content: "",
};

const KnowledgeCore: React.FC = () => {
  const [tab, setTab] = useState<"docs" | "glossary" | "rag">("docs");
  const [docs, setDocs] = useState<CounselCorpusDocMeta[]>([]);
  const [glossary, setGlossary] = useState<CounselGlossaryEntry[]>([]);
  const [ragDocs, setRagDocs] = useState<RagCorpusDoc[]>([]);
  const [ragSummary, setRagSummary] = useState<RagCorpusSummary | null>(null);
  // Not every deployment ships the Counselor Workspace backend (/v1/counsel).
  // When it 404s, hide the counselor tabs instead of showing a load error.
  const [counselAvailable, setCounselAvailable] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const [viewDoc, setViewDoc] = useState<CounselCorpusDoc | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const [glossForm, setGlossForm] = useState({ entryType: "correction", term: "", meaning: "" });

  useEffect(() => {
    refresh();
  }, []);

  const refresh = async () => {
    try {
      const [d, g] = await Promise.all([CounselService.getCorpus(), CounselService.getGlossary()]);
      setDocs(d);
      setGlossary(g);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setCounselAvailable(false);
        setTab("rag");
      } else {
        setError(e?.response?.data?.error?.message || "Could not load the knowledge core");
      }
    }
    // RAG corpus loads independently — a failure here must not blank the counselor corpus
    try {
      const r: any = await ApiService.getRagCorpus();
      setRagDocs(r?.data?.data || []);
      setRagSummary(r?.data?.summary || null);
    } catch {
      setRagDocs([]);
      setRagSummary(null);
    }
  };

  const activeTokens = docs
    .filter((d) => d.Status === "active")
    .reduce((sum, d) => sum + (d.TokenEstimate || 0), 0);

  const openDoc = async (docKey: string) => {
    try {
      setViewDoc(await CounselService.getCorpusDoc(docKey));
    } catch {
      setError("Could not load the document");
    }
  };

  const toggleStatus = async (doc: CounselCorpusDocMeta) => {
    try {
      await CounselService.updateCorpusDoc(doc.DocKey, {
        status: doc.Status === "active" ? "inactive" : "active",
      });
      setInfo(`"${doc.Title}" is now ${doc.Status === "active" ? "inactive" : "active"}.`);
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || "Update failed");
    }
  };

  const submitAdd = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      setError("Title and content are required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await CounselService.createCorpusDoc({
        title: form.title.trim(),
        content: form.content,
        docType: form.docType,
        callType: form.callType || undefined,
        segment: form.segment || undefined,
        studentLabel: form.studentLabel || undefined,
        gradeLabel: form.gradeLabel || undefined,
      });
      setShowAdd(false);
      setForm({ ...emptyForm });
      setInfo("Document added to the knowledge core.");
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || "Could not add the document");
    } finally {
      setSaving(false);
    }
  };

  const submitGlossary = async () => {
    if (!glossForm.term.trim() || !glossForm.meaning.trim()) {
      setError("Term and meaning are required");
      return;
    }
    try {
      await CounselService.addGlossaryEntry(
        glossForm.entryType,
        glossForm.term.trim(),
        glossForm.meaning.trim(),
      );
      setGlossForm({ entryType: "correction", term: "", meaning: "" });
      refresh();
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || "Could not add the entry");
    }
  };

  const toggleGlossary = async (entry: CounselGlossaryEntry) => {
    try {
      await CounselService.updateGlossaryEntry(entry.Id, { active: !entry.Active });
      refresh();
    } catch {
      setError("Update failed");
    }
  };

  return (
    <div className="knowledge-core">
      <header className="kc-header">
        <div>
          <h1>Knowledge Core</h1>
          <p>
            {counselAvailable
              ? "The corpora that ground AI features — counselor documents and glossary, plus the vector-indexed documents used by learning modules and pathways"
              : "The vector-indexed documents that ground AI retrieval in learning modules and pathways"}
          </p>
        </div>
        {counselAvailable ? (
          <div className="kc-stats">
            <div className="kc-stat">
              <div className="kc-stat-num">{docs.filter((d) => d.Status === "active").length}</div>
              <div className="kc-stat-label">active docs</div>
            </div>
            <div className="kc-stat">
              <div className="kc-stat-num">{Math.round(activeTokens / 1000)}k</div>
              <div className="kc-stat-label">tokens in context</div>
            </div>
          </div>
        ) : (
          <div className="kc-stats">
            <div className="kc-stat">
              <div className="kc-stat-num">{ragSummary?.indexed ?? 0}</div>
              <div className="kc-stat-label">indexed docs</div>
            </div>
            <div className="kc-stat">
              <div className="kc-stat-num">
                {((ragSummary?.totalVectors ?? 0) / 1000).toFixed(1)}k
              </div>
              <div className="kc-stat-label">vectors</div>
            </div>
          </div>
        )}
      </header>

      {!!error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {!!info && (
        <Alert variant="success" onClose={() => setInfo(null)} dismissible>
          {info}
        </Alert>
      )}

      <div className="kc-tabs">
        {counselAvailable && (
          <>
            <button
              type="button"
              className={tab === "docs" ? "on" : ""}
              onClick={() => setTab("docs")}
            >
              Documents ({docs.length})
            </button>
            <button
              type="button"
              className={tab === "glossary" ? "on" : ""}
              onClick={() => setTab("glossary")}
            >
              Glossary ({glossary.length})
            </button>
          </>
        )}
        <button type="button" className={tab === "rag" ? "on" : ""} onClick={() => setTab("rag")}>
          Indexed documents ({ragDocs.length})
        </button>
        {tab === "docs" && counselAvailable && (
          <Button variant="primary" size="sm" className="kc-add-btn" onClick={() => setShowAdd(true)}>
            + Add document
          </Button>
        )}
      </div>

      {tab === "docs" && (
        <div className="kc-card">
          <table className="kc-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>Segment</th>
                <th>Student</th>
                <th>~Tokens</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.Id} className={d.Status === "inactive" ? "inactive" : ""}>
                  <td className="kc-td-title" title={d.Title}>
                    <span className="kc-link" onClick={() => openDoc(d.DocKey)}>
                      {d.Title}
                    </span>
                  </td>
                  <td>{TYPE_LABEL[d.DocType] || d.DocType}</td>
                  <td>{d.Segment || "—"}</td>
                  <td className="kc-td-student" title={d.StudentLabel || ""}>
                    {d.StudentLabel || "—"}
                  </td>
                  <td>{d.TokenEstimate ?? "—"}</td>
                  <td>
                    <span className={`kc-badge ${d.Status}`}>{d.Status}</span>
                  </td>
                  <td>
                    <Button
                      variant={d.Status === "active" ? "secondary" : "primary"}
                      size="sm"
                      onClick={() => toggleStatus(d)}
                    >
                      {d.Status === "active" ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "rag" && (
        <div className="kc-card">
          <div className="kc-rag-summary">
            {ragSummary
              ? `${ragSummary.indexed} of ${ragSummary.documents} documents indexed · ${ragSummary.totalVectors.toLocaleString()} vectors · ${ragSummary.modules} learning modules` +
                (ragSummary.failed ? ` · ${ragSummary.failed} failed` : "")
              : "The RAG document index — vector-searchable sources that ground AI retrieval in learning modules and pathways."}
          </div>
          <table className="kc-table">
            <thead>
              <tr>
                <th>Document</th>
                <th>Type</th>
                <th>Learning module</th>
                <th>Vectors</th>
                <th>Status</th>
                <th>Indexed</th>
              </tr>
            </thead>
            <tbody>
              {ragDocs.map((d) => (
                <tr key={d.studyResourceId} className={d.status === "failed" ? "inactive" : ""}>
                  <td
                    className="kc-td-title"
                    title={d.errorMessage || d.documentName || undefined}
                  >
                    {d.documentName || `Resource ${d.studyResourceId}`}
                  </td>
                  <td>{d.resourceType || "—"}</td>
                  <td>{d.moduleName || `Module ${d.lessonPlanId}`}</td>
                  <td>{d.status === "completed" ? d.vectors.toLocaleString() : "—"}</td>
                  <td>
                    <span className={`kc-badge ${d.status}`}>{d.status}</span>
                  </td>
                  <td>{d.indexedAt ? new Date(d.indexedAt).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
              {ragDocs.length === 0 && (
                <tr>
                  <td colSpan={6} className="kc-rag-empty">
                    Nothing indexed yet — open a learning module and use “RAGify” to add its
                    documents to the corpus.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "glossary" && (
        <div className="kc-card">
          <div className="kc-gloss-form">
            <Form.Select
              value={glossForm.entryType}
              onChange={(e) => setGlossForm({ ...glossForm, entryType: e.target.value })}
            >
              <option value="correction">Correction</option>
              <option value="warning">Warning</option>
              <option value="team">Team member</option>
            </Form.Select>
            <Form.Control
              placeholder="Term / name"
              value={glossForm.term}
              onChange={(e) => setGlossForm({ ...glossForm, term: e.target.value })}
            />
            <Form.Control
              placeholder="Correction / meaning / role"
              value={glossForm.meaning}
              onChange={(e) => setGlossForm({ ...glossForm, meaning: e.target.value })}
            />
            <Button variant="primary" onClick={submitGlossary}>
              Add
            </Button>
          </div>
          <table className="kc-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Term</th>
                <th>Meaning</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {glossary.map((g) => (
                <tr key={g.Id} className={g.Active ? "" : "inactive"}>
                  <td>
                    <span className={`kc-badge ${g.EntryType}`}>{g.EntryType}</span>
                  </td>
                  <td>{g.Term}</td>
                  <td className="kc-td-meaning" title={g.Meaning}>
                    {g.Meaning}
                  </td>
                  <td>
                    <Button variant="secondary" size="sm" onClick={() => toggleGlossary(g)}>
                      {g.Active ? "Deactivate" : "Activate"}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* view document modal */}
      <Modal
        show={!!viewDoc}
        onHide={() => setViewDoc(null)}
        size="lg"
        dialogClassName="kc-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {viewDoc?.Title}
            <div className="kc-modal-sub">
              {[viewDoc?.DocType && TYPE_LABEL[viewDoc.DocType], viewDoc?.CallType, viewDoc?.Segment, viewDoc?.GradeLabel]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="kc-doc-content">{viewDoc?.Content}</pre>
        </Modal.Body>
      </Modal>

      {/* add document modal */}
      <Modal
        show={showAdd}
        onHide={() => setShowAdd(false)}
        size="lg"
        backdrop="static"
        dialogClassName="kc-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Add document</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="kc-form-grid">
            <Form.Group className="kc-span2">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Ananya — first strategy call"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Type</Form.Label>
              <Form.Select
                value={form.docType}
                onChange={(e) => setForm({ ...form, docType: e.target.value })}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Call type</Form.Label>
              <Form.Select
                value={form.callType}
                onChange={(e) => setForm({ ...form, callType: e.target.value })}
              >
                <option value="">—</option>
                <option value="discovery">Discovery</option>
                <option value="strategy">Strategy</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>Segment</Form.Label>
              <Form.Control
                value={form.segment}
                onChange={(e) => setForm({ ...form, segment: e.target.value })}
                placeholder="e.g. UG abroad"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Student</Form.Label>
              <Form.Control
                value={form.studentLabel}
                onChange={(e) => setForm({ ...form, studentLabel: e.target.value })}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Grade / stage</Form.Label>
              <Form.Control
                value={form.gradeLabel}
                onChange={(e) => setForm({ ...form, gradeLabel: e.target.value })}
                placeholder="e.g. 11 (CBSE)"
              />
            </Form.Group>
            <Form.Group className="kc-span2">
              <Form.Label>Content</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                placeholder="Paste the call summary / transcript text…"
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submitAdd} disabled={saving}>
            {saving ? "Saving…" : "Add to knowledge core"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default KnowledgeCore;
